import EmailIcon from '@mui/icons-material/Email';
import HttpsIcon from '@mui/icons-material/Https';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled';
import { Box, Checkbox, tooltipClasses, Typography } from '@mui/material';
import clsx from 'clsx';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SmsStatusTone } from 'shared/types/Events';
import { getSmsStatusDisplay } from 'shared/utils/events';
import moment from 'shared/utils/moment';

import { TickIcon } from '@/assets/svg';
import ReadTickIcon from '@/assets/svg/ReadTickIcon';
import { BlockedPremiumFeatureModal, Tooltip } from '@/components';
import { useDBSyncContext } from '@/context/DBSyncContext';
import { useNetwork } from '@/hooks';
import { useIsPremiumFeatureAvailable } from '@/hooks/useIsPremiumFeatureAvailable';
import { COLOR, ENTITY_COLOR } from '@/theme/colors';

import TooltipSyncButton from '../TooltipButton';

import { Props } from './types';

const TONE_COLOR: Record<SmsStatusTone, string> = {
    neutral: COLOR.ghost,
    info: COLOR.heartPurple,
    success: COLOR.cozumel,
    warning: COLOR.warningYellow,
    error: COLOR.errorRed,
};

function AppointmentInviteContactSection({
    item,
    onSmsClick,
    onEmailClick,
    onEmailDisabledClick,
    onSmsDisabledClick,
    state,
    disable = false,
    mode = 'edit',
}: Props) {
    const [
        showBlockedPremiumFeaturesModal,
        setShowBlockedPremiumFeaturesModal,
    ] = useState(false);
    const { t } = useTranslation();

    const { isSyncInProgress } = useDBSyncContext();
    const { isPremiumFeatureAvailable } = useIsPremiumFeatureAvailable();

    const isOnline = useNetwork();

    const contactState = useMemo(
        () =>
            state[item.id] ?? {
                smsStatus: 'not_sent',
                emailStatus: 'not_sent',
                smsAt: null,
                emailAt: null,
                sms: false,
                email: false,
            },
        [item.id, state],
    );

    const smsDisplay = useMemo(
        () =>
            contactState.smsDeliveryStatus
                ? getSmsStatusDisplay(
                      contactState.smsDeliveryStatus,
                      contactState.sms,
                      contactState.smsAt ?? '',
                  )
                : null,
        [contactState.smsDeliveryStatus, contactState.sms, contactState.smsAt],
    );

    const isSmsSending = contactState.smsStatus === 'sending';
    const isEmailSending = contactState.emailStatus === 'sending';

    const isSmsSent = contactState.smsStatus === 'sent';
    const isEmailSent = contactState.emailStatus === 'sent';

    const isSmsNotSent = contactState.smsStatus === 'not_sent';
    const isEmailNotSent = contactState.emailStatus === 'not_sent';

    const isSmsDisabled = !item.phone || (disable && isSmsNotSent);
    const isEmailDisabled = !item.email || (disable && isEmailNotSent);

    const isEditMode = mode === 'edit';

    const smsSentAt = useMemo(
        () =>
            contactState.smsAt
                ? moment(contactState.smsAt).format('MMMM Do H[:]mm a')
                : '',
        [contactState.smsAt],
    );

    const emailSentAt = useMemo(
        () =>
            contactState.emailAt
                ? moment(contactState.emailAt).format('MMMM Do H[:]mm a')
                : '',
        [contactState.emailAt],
    );

    const closeBlockedPremiumFeaturesModal = useCallback(
        () => setShowBlockedPremiumFeaturesModal(false),
        [],
    );

    const openBlockedPremiumFeaturesModal = useCallback(
        () => setShowBlockedPremiumFeaturesModal(true),
        [],
    );

    const handleSmsClick = useCallback(
        (event) => {
            if (!isPremiumFeatureAvailable) {
                openBlockedPremiumFeaturesModal();
                return event.stopPropagation();
            }
            if (!isSmsSending && !isSmsSent) {
                event.stopPropagation();
            }
            if (isSmsDisabled) {
                onSmsDisabledClick(item);
            } else if (!isSmsSending && !isSmsSent) {
                !disable && onSmsClick(item);
            }
        },
        [
            isPremiumFeatureAvailable,
            isSmsSending,
            isSmsSent,
            isSmsDisabled,
            openBlockedPremiumFeaturesModal,
            onSmsDisabledClick,
            item,
            disable,
            onSmsClick,
        ],
    );

    const handleEmailClick = useCallback(
        (event) => {
            if (!isPremiumFeatureAvailable) {
                openBlockedPremiumFeaturesModal();
                return event.stopPropagation();
            }
            if (!isEmailSent && !isEmailSending) {
                event.stopPropagation();
            }
            if (isEmailDisabled) {
                onEmailDisabledClick(item);
            } else if (!isEmailSent && !isEmailSending) {
                !disable && onEmailClick(item);
            }
        },
        [
            isPremiumFeatureAvailable,
            isEmailDisabled,
            isEmailSent,
            isEmailSending,
            openBlockedPremiumFeaturesModal,
            onEmailDisabledClick,
            item,
            disable,
            onEmailClick,
        ],
    );

    const renderActionElement = useCallback(
        (type: 'sms' | 'email') => {
            const isTypeSms = type === 'sms';

            const status = isTypeSms
                ? contactState.smsStatus
                : contactState.emailStatus;

            return status === 'not_sent' ? (
                isEditMode ? (
                    <Checkbox
                        checked={
                            isTypeSms ? contactState.sms : contactState.email
                        }
                        disabled={isTypeSms ? isSmsDisabled : isEmailDisabled}
                        size="small"
                        className="ml-[-9px] mr-[-6px]"
                    />
                ) : (
                    <Box className="py-[9px]">
                        <TooltipSyncButton
                            tooltip={t('AddEditModalHeader:button_tooltip', {
                                defaultValue:
                                    'Please wait for the synchronization to complete',
                            })}
                            showTooltip={
                                (isTypeSms
                                    ? !isSmsDisabled
                                    : !isEmailDisabled) && isSyncInProgress
                            }
                            onClick={
                                isTypeSms ? handleSmsClick : handleEmailClick
                            }
                            size="small"
                            variant="outlined"
                            sx={{ color: ENTITY_COLOR.schedule }}
                            disabled={
                                isSyncInProgress ||
                                (isTypeSms ? isSmsDisabled : isEmailDisabled)
                            }
                            startIcon={
                                !isTypeSms ? (
                                    <EmailIcon
                                        htmlColor={
                                            isEmailDisabled || isSyncInProgress
                                                ? COLOR.ghost
                                                : ENTITY_COLOR.schedule
                                        }
                                        className="w-[20px] h-[20px]"
                                    />
                                ) : (
                                    <SmartphoneIcon
                                        htmlColor={
                                            isSmsDisabled || isSyncInProgress
                                                ? COLOR.ghost
                                                : ENTITY_COLOR.schedule
                                        }
                                        className="w-[20px] h-[20px]"
                                    />
                                )
                            }
                        >
                            {t(
                                isTypeSms
                                    ? 'Event:invite_contact:invite_via_sms'
                                    : 'Event:invite_contact:invite_via_email',
                            )}
                        </TooltipSyncButton>
                    </Box>
                )
            ) : status === 'sent' ? (
                <Box className="mr-1 py-[9px]">
                    <ReadTickIcon
                        width={20}
                        height={20}
                        color={COLOR.cozumel}
                    />
                </Box>
            ) : isOnline ? (
                <Box className="py-[9px]">
                    <TickIcon color={COLOR.ghost} width={20} height={20} />
                </Box>
            ) : (
                <Box className="py-[9px]">
                    <SyncDisabledIcon
                        className="w-[20px] h-[20px]"
                        htmlColor={COLOR.heartPurple}
                    />
                </Box>
            );
        },
        [
            contactState.email,
            contactState.emailStatus,
            contactState.sms,
            contactState.smsStatus,
            handleEmailClick,
            handleSmsClick,
            isEditMode,
            isEmailDisabled,
            isOnline,
            isSmsDisabled,
            isSyncInProgress,
            t,
        ],
    );

    const smsContent = useMemo(
        () => (
            <Box
                className={clsx('flex items-center', {
                    'cursor-pointer': isSmsDisabled || isSmsNotSent,
                })}
                onClick={handleSmsClick}
            >
                {smsDisplay?.labelKey ? (
                    <>
                        <SmartphoneIcon
                            htmlColor={TONE_COLOR[smsDisplay.tone]}
                            className="mr-[4px] w-[20px] h-[20px]"
                        />
                        <Typography
                            variant="body2"
                            sx={{ color: TONE_COLOR[smsDisplay.tone] }}
                            fontSize={14}
                        >
                            {t(smsDisplay.labelKey)}
                        </Typography>
                    </>
                ) : (
                    <>
                        {renderActionElement('sms')}
                        {isEditMode || !isSmsNotSent ? (
                            <>
                                <SmartphoneIcon
                                    htmlColor={
                                        (!isPremiumFeatureAvailable ||
                                            isSmsDisabled ||
                                            isSmsSending) &&
                                        isOnline
                                            ? COLOR.ghost
                                            : isSmsSending
                                            ? COLOR.heartPurple
                                            : isSmsSent
                                            ? COLOR.cozumel
                                            : undefined
                                    }
                                    className="mr-[4px] w-[20px] h-[20px]"
                                />
                                <Typography
                                    variant="body2"
                                    className={clsx({
                                        'text-ghost':
                                            !isPremiumFeatureAvailable ||
                                            isSmsDisabled ||
                                            (isSmsSending && isOnline),
                                    })}
                                    fontSize={isSmsSending ? 12 : 14}
                                >
                                    {t(
                                        isOnline && isSmsSending
                                            ? 'Event:invite_contact:sending'
                                            : isSmsSending
                                            ? 'Event:invite_contact:pending'
                                            : 'Event:invite_contact:sms',
                                    )}
                                </Typography>
                            </>
                        ) : null}
                    </>
                )}
            </Box>
        ),
        [
            isSmsDisabled,
            isSmsNotSent,
            handleSmsClick,
            renderActionElement,
            isEditMode,
            isPremiumFeatureAvailable,
            isSmsSending,
            isOnline,
            isSmsSent,
            smsDisplay,
            t,
        ],
    );

    const emailContent = useMemo(
        () => (
            <Box
                className={clsx('flex items-center', {
                    'cursor-pointer': isEmailDisabled || isEmailNotSent,
                })}
                onClick={handleEmailClick}
            >
                {renderActionElement('email')}
                {isEditMode || !isEmailNotSent ? (
                    <>
                        <EmailIcon
                            htmlColor={
                                (!isPremiumFeatureAvailable ||
                                    isEmailDisabled ||
                                    isEmailSending) &&
                                isOnline
                                    ? COLOR.ghost
                                    : isEmailSending
                                    ? COLOR.heartPurple
                                    : isEmailSent
                                    ? COLOR.cozumel
                                    : undefined
                            }
                            className="mr-[4px] w-[20px] h-[20px]"
                        />
                        <Typography
                            variant="body2"
                            fontSize={isEmailSending ? 12 : 14}
                            className={clsx({
                                'text-ghost':
                                    (!isPremiumFeatureAvailable ||
                                        isEmailDisabled ||
                                        isEmailSending) &&
                                    isOnline,
                            })}
                        >
                            {t(
                                isOnline && isEmailSending
                                    ? 'Event:invite_contact:sending'
                                    : isEmailSending
                                    ? 'Event:invite_contact:pending'
                                    : 'Event:invite_contact:email',
                            )}
                        </Typography>
                    </>
                ) : null}
            </Box>
        ),
        [
            isEmailDisabled,
            isEmailNotSent,
            handleEmailClick,
            renderActionElement,
            isEditMode,
            isPremiumFeatureAvailable,
            isEmailSending,
            isOnline,
            isEmailSent,
            t,
        ],
    );

    return (
        <Box
            className={clsx('flex gap-2 items-center mb-[5px]', {
                'ml-[91px]': !isEditMode,
                'ml-4': isEditMode,
            })}
        >
            {isEditMode ? (
                <>
                    {!isPremiumFeatureAvailable && (
                        <HttpsIcon
                            style={{
                                width: 16,
                                height: 16,
                                color: COLOR.deepCerulean,
                            }}
                        />
                    )}
                    <Typography
                        noWrap
                        className="mr-2 font-bold"
                        variant="body2"
                    >
                        {t('Event:invite_contact:title')}
                    </Typography>
                </>
            ) : null}
            {smsDisplay?.helperKey ? (
                <Tooltip
                    title={t(smsDisplay.helperKey, {
                        type: 'SMS',
                        contactDetails: `${item.phonePrefix ?? ''}${
                            item.phone
                        }`,
                        date: smsSentAt,
                    })}
                    placement="bottom"
                    PopperProps={{
                        sx: {
                            [`& .${tooltipClasses.tooltip}`]: {
                                maxWidth: 'min-content',
                                minWidth: 300,
                            },
                        },
                    }}
                >
                    {smsContent}
                </Tooltip>
            ) : isSmsSent || (isSmsSending && !isOnline) ? (
                <Tooltip
                    title={t(
                        isSmsSending && !isOnline
                            ? 'Event:invite_contact:offline_tooltip'
                            : 'Event:invite_contact:sms_tooltip',
                        {
                            phone: `${item.phonePrefix ?? ''}${item.phone}`,
                            date: smsSentAt,
                        },
                    )}
                    placement="bottom"
                    PopperProps={{
                        sx: {
                            [`& .${tooltipClasses.tooltip}`]: {
                                maxWidth: 'min-content',
                                minWidth: 300,
                            },
                        },
                    }}
                >
                    {smsContent}
                </Tooltip>
            ) : (
                smsContent
            )}
            {isEmailSent || (isEmailSending && !isOnline) ? (
                <Tooltip
                    title={t(
                        isEmailSending && !isOnline
                            ? 'Event:invite_contact:offline_tooltip'
                            : 'Event:invite_contact:email_tooltip',
                        {
                            email: item.email,
                            date: emailSentAt,
                        },
                    )}
                    placement="bottom"
                    PopperProps={{
                        sx: {
                            [`& .${tooltipClasses.tooltip}`]: {
                                maxWidth: 'min-content',
                                minWidth: 300,
                            },
                        },
                    }}
                >
                    {emailContent}
                </Tooltip>
            ) : (
                emailContent
            )}

            {showBlockedPremiumFeaturesModal ? (
                <BlockedPremiumFeatureModal
                    close={closeBlockedPremiumFeaturesModal}
                />
            ) : null}
        </Box>
    );
}

export default AppointmentInviteContactSection;
